// The Manuals library's "Manufacturer names" card (Session BAV real library run, 8 October 2026). The library files every
// document under a manufacturer taken from where it sits: a Google Takeout zip's own name ("Ace-20261007T185322Z-1-001.zip"
// is Ace), then the folders inside it. Here the office corrects how a name is read, with nothing read again:
//   - "Not a manufacturer" marks a name as a group (Work manuals, autodialers): the folders inside it become the manufacturers;
//   - "Rename" shows another name (renaming two names to one merges them, "Macpursa" into "Macpuarsa");
//   - "Back to default" removes the correction.
// Every change is previewed first (how many files would move) and saved through a two-step confirm.
import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { friendlyApiError } from "../../lib/friendlyApiError";
import { Button } from "../../components/Button";
import { ConfirmButton } from "../../components/ConfirmButton";
import { Loader } from "../../components/Loader";
import { inputCls, labelCls } from "./knowledgeUi";
import type { KnowledgeLibraryName, KnowledgeLibraryNameInput } from "../../lib/knowledgeTypes";

const QUIET_BTN =
  "rounded-ele border border-ele-purple-accent px-3 py-1.5 text-sm text-ele-strong hover:bg-ele-surface-raised disabled:opacity-50";

interface Draft {
  name: KnowledgeLibraryName;
  isGroup: boolean;
  renameTo: string;
  preview: string | null;
}

export function KnowledgeLibraryNamesCard() {
  const [names, setNames] = useState<KnowledgeLibraryName[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState("");
  const [draft, setDraft] = useState<Draft | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setNames((await api.knowledge.drive.manufacturers()).names);
      setError(null);
    } catch (err) {
      setError(friendlyApiError(err, "The library's names could not be loaded."));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const shown = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return (names ?? []).filter((n) => !q || n.name.toLowerCase().includes(q) || n.shownAs.toLowerCase().includes(q) || (n.group ?? "").toLowerCase().includes(q));
  }, [names, filter]);

  function input(d: Draft): KnowledgeLibraryNameInput {
    const groupChoice = d.isGroup === d.name.isGroupByDefault ? null : d.isGroup;
    return { name: d.name.name, isGroup: groupChoice, renameTo: d.renameTo.trim() || null };
  }

  async function check(d: Draft) {
    setBusy(true);
    setError(null);
    try {
      const res = await api.knowledge.drive.previewManufacturerName(input(d));
      setDraft({ ...d, preview: res.message });
    } catch (err) {
      setError(friendlyApiError(err, "The change could not be checked."));
    } finally {
      setBusy(false);
    }
  }

  async function save(d: Draft) {
    setBusy(true);
    setError(null);
    try {
      const res = await api.knowledge.drive.saveManufacturerName(input(d));
      setNames(res.names);
      setMessage(res.message);
      setDraft(null);
    } catch (err) {
      setError(friendlyApiError(err, "The change could not be saved."));
    } finally {
      setBusy(false);
    }
  }

  async function clear(n: KnowledgeLibraryName) {
    setBusy(true);
    setError(null);
    try {
      const res = await api.knowledge.drive.clearManufacturerName(n.key);
      setNames(res.names);
      setMessage(res.message);
    } catch (err) {
      setError(friendlyApiError(err, "The correction could not be removed."));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="rounded-ele border border-ele-purple-accent bg-ele-surface p-4" aria-labelledby="kb-names-heading">
      <h2 id="kb-names-heading" className="font-heading text-lg text-ele-blue">
        Manufacturer names
      </h2>
      <p className="mt-1 text-sm text-ele-body">
        Each file is filed under the name of the zip it came in, or the folder it sits in. Mark a name that is not a manufacturer (such as Work manuals) so the
        folders inside it are used instead, or rename a name. Nothing is read again: every file moves at once.
      </p>
      {message && (
        <p role="status" className="mt-2 text-sm text-ele-strong">
          {message}
        </p>
      )}
      {error && (
        <p role="alert" className="mt-2 rounded-ele border border-ele-danger bg-ele-surface px-3 py-2 text-sm text-ele-strong">
          {error}
        </p>
      )}
      {names === null ? (
        error ? null : <Loader label="Loading the names" />
      ) : names.length === 0 ? (
        <p className="mt-3 text-sm text-ele-body">No files have been read from the library yet.</p>
      ) : (
        <>
          <label className="mt-3 block max-w-sm">
            <span className={labelCls}>Find a name</span>
            <input aria-label="Find a name" value={filter} onChange={(e) => setFilter(e.target.value)} className={inputCls} />
          </label>
          <table className="ele-list-table mt-3 w-full text-sm">
            <thead>
              <tr>
                <th scope="col">Name in the library</th>
                <th scope="col">Shown as</th>
                <th scope="col">Files</th>
                <th scope="col">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {shown.map((n) => (
                <tr key={`${n.level}|${n.group ?? ""}|${n.key}`}>
                  <td className="truncate" title={n.group ? `${n.group} / ${n.name}` : n.name}>
                    {n.group ? <span className="text-ele-body opacity-80">{n.group} / </span> : null}
                    {n.name}
                    {n.isGroup && <span className="ml-2 text-xs text-ele-body">(not a manufacturer: its folders are used)</span>}
                  </td>
                  <td className="truncate">{n.isGroup ? "Its folders" : n.shownAs}</td>
                  <td>{n.files.toLocaleString("en-GB")}</td>
                  <td className="whitespace-nowrap text-right">
                    <button
                      type="button"
                      className={QUIET_BTN}
                      onClick={() => setDraft({ name: n, isGroup: n.isGroup, renameTo: n.renameTo ?? "", preview: null })}
                      disabled={busy}
                    >
                      Change
                    </button>
                    {(n.isGroupSetByOffice !== null || n.renameTo) && (
                      <ConfirmButton label="Back to default" confirmLabel={`Read "${n.name}" by default again?`} onConfirm={() => clear(n)} disabled={busy} />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {draft && (
        <div className="mt-4 rounded-ele border border-ele-purple-accent p-3" role="group" aria-label={`Change ${draft.name.name}`}>
          <p className="text-sm text-ele-strong">
            Change how <strong>{draft.name.name}</strong> is read
          </p>
          <label className="mt-2 flex items-center gap-2 text-sm text-ele-strong">
            <input type="checkbox" checked={draft.isGroup} onChange={(e) => setDraft({ ...draft, isGroup: e.target.checked, preview: null })} />
            Not a manufacturer: use the folders inside it
          </label>
          <label className="mt-2 block max-w-sm">
            <span className={labelCls}>Show it as</span>
            <input
              aria-label="Show it as"
              value={draft.renameTo}
              onChange={(e) => setDraft({ ...draft, renameTo: e.target.value, preview: null })}
              placeholder={`Blank to keep "${draft.name.name}"`}
              className={inputCls}
            />
          </label>
          {draft.preview && (
            <p role="status" className="mt-2 text-sm text-ele-strong">
              {draft.preview}
            </p>
          )}
          <div className="mt-3 flex flex-wrap gap-2">
            {!draft.preview ? (
              <Button type="button" onClick={() => void check(draft)} loading={busy} disabled={busy}>
                Check the change
              </Button>
            ) : (
              <ConfirmButton label="Save the change" confirmLabel="Save it now?" onConfirm={() => save(draft)} disabled={busy} />
            )}
            <button type="button" className={QUIET_BTN} onClick={() => setDraft(null)} disabled={busy}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
